import React from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarLayout from './SidebarLayout';
import {
  Clock, CalendarDays, Users, Receipt, Crown, Calendar, Package, BookOpen, GitPullRequest, ScanBarcode, HeartHandshake, Lock,
} from 'lucide-react';
import { useClockGate } from '../../hooks/useClockGate';

// Compact "you're clocked out" banner shown to field employees while they have no
// active clock entry. Non-blocking — it only nudges them toward the Time Clock.
const ClockOutBanner = () => {
  const { required, clockedIn } = useClockGate();
  const navigate = useNavigate();
  if (!required || clockedIn !== false) return null;
  return (
    <div data-testid="clock-out-banner" className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/30 px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 flex-wrap">
      <p className="text-sm text-[#F3E5AB] font-medium flex items-center gap-2">
        <Lock className="w-4 h-4 shrink-0" />
        You&apos;re not clocked in. Clock in to view today&apos;s schedule.
      </p>
      <button
        type="button"
        data-testid="clock-out-banner-clock-in"
        onClick={() => navigate('/employee/timeclock')}
        className="text-xs font-bold px-3 py-1.5 rounded-md bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] transition-colors"
      >
        Clock In
      </button>
    </div>
  );
};

const EmployeeLayout = () => {
  const { required, clockedIn } = useClockGate();
  // Show a lock on the Field Schedule nav item when a field employee is clocked out.
  const scheduleLocked = required && clockedIn === false;

  const sections = [
    {
      label: 'My Work',
      items: [
        { path: '/employee/timeclock', label: 'My Timesheet', icon: Clock },
        {
          path: '/employee/schedule',
          label: scheduleLocked ? 'Field Schedule 🔒' : 'Field Schedule',
          icon: scheduleLocked ? Lock : CalendarDays,
        },
        { path: '/employee/customers', label: 'Customers', icon: Users },
      ],
    },
    {
      label: 'Tools',
      items: [
        { path: '/employee/billing', label: 'Billing', icon: Receipt },
        { path: '/employee/members', label: 'Members', icon: Crown },
        { path: '/employee/inventory-scanner', label: 'Inventory Scanner', icon: ScanBarcode },
        { path: '/employee/service-catalog', label: 'Service Catalog', icon: BookOpen },
        { path: '/employee/knowledge-base', label: 'Knowledge Base', icon: BookOpen },
      ],
    },
    {
      label: 'Me',
      items: [
        { path: '/employee/timeoff', label: 'Time Off', icon: Calendar },
        { path: '/employee/benefits', label: 'My Benefits', icon: HeartHandshake },
        { path: '/employee/my-changes', label: 'My Changes', icon: GitPullRequest },
      ],
    },
  ];

  return (
    <SidebarLayout
      sections={sections}
      portalLabel="Field Crew"
      showDepartmentSwitcher={false}
      testIdPrefix="employee"
      topBanner={<ClockOutBanner />}
    />
  );
};

export default EmployeeLayout;
